// TuneCell renders the main per-degree pitch editor in the scale table.
// It coordinates scala-entry drafting, rationalisation UI, HEJI display, and
// live preview hooks for one degree without owning the full table state.

import { Component } from "preact";

import { useState, useRef, useCallback, useEffect, useMemo } from "preact/hooks";
import { parseExactInterval } from "../../../tuning/interval.js";
import { spelledHejiLabel } from "../../../notation/key-label.js";
import { createReferenceFrame } from "../../../notation/reference-frame.js";
import {
  getRationalisationRequest,
  getHumanTestableRationalCandidates,
  getSaveString,
  formatRatioText,
  formatPrimeLimits,
} from "./rationalise.js";
import { parseOptionalPositiveInt } from "./search-prefs.js";
import {
  clearDegreePreview,
  getCommittedDegreeCents,
  getDegreePreview,
  isDegreeComparing,
  setDegreeComparing,
  setDegreePreview,
} from "../../../tuning/tuning-preview-runtime.js";

function modulo(value, modulus) {
  return ((value % modulus) + modulus) % modulus;
}

function candidateHejiLabel(candidate, settings, hejiAnchorLabelEff, hejiAnchorRatioEff) {
  const anchorLabel = hejiAnchorLabelEff ?? settings?.heji_anchor_label ?? "A";
  const anchorRatioText = hejiAnchorRatioEff ?? settings?.heji_anchor_ratio ?? "1/1";
  try {
    const frame = createReferenceFrame({
      anchorLabel,
      anchorRatio: anchorRatioText,
      anchorOctave: 4,
    });
    const anchorCents = parseExactInterval(anchorRatioText)?.cents ?? 0;
    const centsFromAnchor = modulo((candidate?.cents ?? 0) - anchorCents, 1200);
    return spelledHejiLabel(frame, candidate?.ratioText ?? null, centsFromAnchor, {
      suppressDeviation: true,
    });
  } catch {
    return null;
  }
}

/**
 * TuneCell — drag-to-tune control for a single scale degree.
 * Drag left/right to retune; A/B compare; save or revert through one
 * contextual action button, matching the ColorCell UX.
 *
 * When retuning the reference_degree, behavior depends on retuning_mode:
 * - 'recalculate_reference' (default): Keep current sound, recalculate Reference Frequency
 * - 'transpose_scale': Transpose entire scale, preserve Reference Frequency
 *
 * retuning_mode is intentionally internal for now. There is currently no
 * user-facing toggle in the UI, so Hexatone always runs with the default
 * 'recalculate_reference' behavior. Keep the alternate path in place and
 * documented here in case a dedicated UX control is added later.
 */
export function getEffectivePreviewCents(tunedCents, comparing, originalCents) {
  if (tunedCents === null) return null;
  return comparing ? originalCents : tunedCents;
}

const TuneCell = ({
  scaleStr,
  degree,
  committedInterval,
  committedCents,
  workspace,
  settings,
  frequencyAtDegree,
  searchPrefs,
  keysRef,
  onChange,
  onDegree0Save,
  reference_degree,
  fundamental,
  onFundamentalChange,
  retuning_mode,
  previewState,
  onPreviewChange,
  resetVersion,
  hejiAnchorLabelEff,
  hejiAnchorRatioEff,
}) => {
  const [previewInterval, setPreviewInterval] = useState(null);
  const [rationaliseCandidates, setRationaliseCandidates] = useState(null);
  const dragStart = useRef(null);
  const tuneCellRef = useRef(null);
  // Capture the Keys instance when drag starts — keysRef.current may change
  // during reconciliation, so we need the specific instance we set drag on
  const dragKeysInstance = useRef(null);
  // Keep the latest onPreviewChange in a ref so effects don't re-fire when
  // the parent re-renders and creates a new function reference.
  const onPreviewChangeRef = useRef(onPreviewChange);
  useEffect(() => {
    onPreviewChangeRef.current = onPreviewChange;
  }, [onPreviewChange]);
  const preview = getDegreePreview(previewState, degree);
  const tunedCents = preview?.cents ?? null;
  const comparing = isDegreeComparing(previewState, degree);
  const originalCents = getCommittedDegreeCents(workspace, degree);

  useEffect(() => {
    if (!preview) {
      setPreviewInterval(null);
      setRationaliseCandidates(null);
    }
  }, [scaleStr, committedCents, preview]);

  // When a direct text edit commits a new scale value, discard any in-flight
  // drag state so the TuneCell resets to the newly committed pitch.
  useEffect(() => {
    if (resetVersion === undefined || resetVersion === 0) return;
    onPreviewChangeRef.current?.((prevState) => clearDegreePreview(prevState, degree));
    setPreviewInterval(null);
    setRationaliseCandidates(null);
  }, [resetVersion, degree]);

  useEffect(() => {
    if (!rationaliseCandidates) return;
    const dismiss = (event) => {
      if (tuneCellRef.current?.contains(event.target)) return;
      setRationaliseCandidates(null);
    };
    document.addEventListener("pointerdown", dismiss, true);
    return () => document.removeEventListener("pointerdown", dismiss, true);
  }, [rationaliseCandidates]);

  // Clean up drag state and any in-flight glide on unmount.
  useEffect(() => {
    return () => {
      onPreviewChangeRef.current?.((prevState) => clearDegreePreview(prevState, degree));
      // Use the captured instance from when drag started, not keysRef.current
      // which may now point to a new Keys instance after reconstruction
      if (dragKeysInstance.current && dragKeysInstance.current.setTuneDragging) {
        dragKeysInstance.current.setTuneDragging(false);
      }
    };
  }, [degree]);

  const currentCents = tunedCents !== null ? tunedCents : originalCents;
  const isDirty = tunedCents !== null && Math.abs(tunedCents - originalCents) > 0.001;
  const isReferenceDegree = degree === reference_degree;

  const pushToKeys = useCallback(
    (cents) => {
      if (!keysRef || !keysRef.current) return;
      if (degree === 0) {
        // Only retune degree-0 notes; all other notes stay at their pitch.
        // cents is the absolute offset from 0 (the drag value from originalCents=0).
        if (keysRef.current.previewDegree0) keysRef.current.previewDegree0(cents);
      } else if (keysRef.current.updateScaleDegree) {
        keysRef.current.updateScaleDegree(degree, cents);
      }
    },
    [keysRef, degree],
  );

  const glideTo = useCallback(
    (targetCents) => {
      pushToKeys(targetCents);
    },
    [pushToKeys],
  );

  const openRationaliseCandidates = useCallback(
    (targetCents) => {
      // TuneCell owns transient preview state only. Candidate search reads the
      // committed workspace plus this row's current cents target; committing the
      // chosen ratio still happens through the normal scale update path.
      const request = getRationalisationRequest({
        degree,
        tunedCents: targetCents,
        workspace,
        settings,
        frequencyAtDegree,
        searchPrefs,
      });
      const candidates = getHumanTestableRationalCandidates(request);
      setRationaliseCandidates(candidates.length ? candidates : null);
    },
    [degree, workspace, settings, frequencyAtDegree, searchPrefs],
  );

  const onPointerDown = useCallback(
    (e) => {
      // Set flag BEFORE setPointerCapture — capture triggers a spurious Escape keyup
      // which would drop sustain; the flag guards against that in keys.js.
      // Also capture the Keys instance for cleanup in case we're unmounted mid-drag.
      if (keysRef && keysRef.current && keysRef.current.setTuneDragging) {
        keysRef.current.setTuneDragging(true);
        dragKeysInstance.current = keysRef.current;
      }
      e.currentTarget.setPointerCapture?.(e.pointerId);
      dragStart.current = { lastX: e.clientX, accCents: currentCents };
      openRationaliseCandidates(currentCents);
    },
    [currentCents, keysRef, openRationaliseCandidates],
  );

  const onPointerMove = useCallback(
    (e) => {
      if (!dragStart.current) return;
      const dx = e.clientX - dragStart.current.lastX;
      if (dx === 0) return;
      // Velocity-sensitive: slow drags (|dx| small) → fine; fast drags → coarser.
      // sensitivity = base * speed^1.5 — superlinear so fast moves cover more ground
      const speed = Math.abs(dx);
      const sensitivity = 0.05 * Math.pow(speed, 1.125); // ~0.05¢ at 1px/event, ~1¢ at 7px/event
      const newCents = dragStart.current.accCents + Math.sign(dx) * sensitivity;
      dragStart.current.lastX = e.clientX;
      dragStart.current.accCents = newCents;
      onPreviewChangeRef.current?.((prevState) =>
        setDegreeComparing(setDegreePreview(prevState, degree, newCents), degree, false),
      );
      // Free dragging invalidates any previously snapped exact candidate.
      setPreviewInterval(null);
      setRationaliseCandidates(null);
      // Use glideTo so fast swipes interpolate smoothly rather than jumping.
      glideTo(newCents);
    },
    [degree, glideTo],
  );

  const onPointerUp = useCallback(() => {
    dragStart.current = null;
    if (keysRef && keysRef.current && keysRef.current.setTuneDragging) {
      keysRef.current.setTuneDragging(false);
      // If Escape is still physically held, re-engage sustain now drag is done
      if (keysRef.current.state && keysRef.current.state.escHeld) {
        keysRef.current.sustainOn();
      }
    }
  }, [keysRef]);

  const onCompare = useCallback(() => {
    const next = !comparing;
    onPreviewChangeRef.current?.((prevState) => setDegreeComparing(prevState, degree, next));
    glideTo(next ? originalCents : tunedCents);
  }, [comparing, degree, glideTo, originalCents, tunedCents]);

  const onSave = useCallback(() => {
    const saveVal = tunedCents !== null ? tunedCents : originalCents;
    // getSaveString guards the exact-ratio path: only a still-valid snapped
    // preview writes ratio text, otherwise the current cents value is saved.
    const saveStr = getSaveString({
      committedInterval,
      previewInterval,
      tunedCents,
      committedCents,
    });

    if (degree === 0) {
      // Degree 0 retuning: shift all other scale degrees by -delta so that
      // all notes except degree 0 remain at the same absolute pitch.
      // onDegree0Save receives the delta in cents.
      if (onDegree0Save) onDegree0Save(saveVal); // saveVal === delta (originalCents is 0)
    } else if (isReferenceDegree && retuning_mode !== "transpose_scale") {
      const delta = tunedCents - originalCents;
      const newFundamental = fundamental * Math.pow(2, delta / 1200.0);
      if (onFundamentalChange) {
        onFundamentalChange(newFundamental, saveStr);
      } else {
        onChange(saveStr);
      }
    } else {
      onChange(saveStr);
    }
    onPreviewChangeRef.current?.((prevState) => clearDegreePreview(prevState, degree));
    setPreviewInterval(null);
    setRationaliseCandidates(null);
    // Restore live preview to 0 so held degree-0 notes return to base pitch
    if (degree === 0) pushToKeys(0);
  }, [
    tunedCents,
    degree,
    isReferenceDegree,
    retuning_mode,
    fundamental,
    onFundamentalChange,
    onDegree0Save,
    onChange,
    pushToKeys,
    committedInterval,
    previewInterval,
    committedCents,
    originalCents,
  ]);

  const onRevert = useCallback(() => {
    onPreviewChangeRef.current?.((prevState) => clearDegreePreview(prevState, degree));
    setPreviewInterval(null);
    setRationaliseCandidates(null);
    glideTo(originalCents);
  }, [degree, glideTo, originalCents]);

  const onPrimaryAction = useCallback(() => {
    if (comparing) {
      onRevert();
      return;
    }
    onSave();
  }, [comparing, onRevert, onSave]);

  const delta = isDirty ? tunedCents - originalCents : 0;
  const deltaStr = delta >= 0 ? `+${delta.toFixed(1)}c` : `${delta.toFixed(1)}c`;

  return (
    <div class="tune-cell" ref={tuneCellRef}>
      {isDirty && (
        <span class={`tune-delta${comparing ? " tune-comparing" : ""}`}>
          {comparing ? "orig" : deltaStr}
        </span>
      )}
      {isDirty && (
        <button
          type="button"
          class={`tune-btn${comparing ? " tune-btn--active" : ""}`}
          onClick={onCompare}
          title="A/B compare with original"
        >
          <span class="refresh-glyph tune-btn-compare" aria-hidden="true">
            ⟳
          </span>
        </button>
      )}
      {isDirty && (
        <button
          type="button"
          class="tune-btn tune-btn--save"
          onClick={onPrimaryAction}
          title={comparing ? "Revert to original" : "Save tuning"}
        >
          {comparing ? "x" : "✓"}
        </button>
      )}
      <span
        class="tune-handle tune-handle--padded"
        title="Drag left/right to tune — slow for fine, fast for coarse"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        ⟺
      </span>
      {rationaliseCandidates && (
        <div class="rationalise-dropdown">
          {rationaliseCandidates.map((candidate) => {
            const hejiLabel = candidateHejiLabel(
              candidate,
              settings,
              hejiAnchorLabelEff,
              hejiAnchorRatioEff,
            );
            const tol = parseOptionalPositiveInt(searchPrefs?.centsTolerance) ?? 6;
            const pl = parseOptionalPositiveInt(searchPrefs?.primeLimit) ?? 19;
            const region = searchPrefs?.region ?? "symmetric";
            const outsideTolerance = Math.abs(candidate.deviation) > tol;
            const outsidePrimeLimit = candidate.primeLimit != null && candidate.primeLimit > pl;
            // For overtonal region: any negative non-2 exponent in the monzo means the
            // ratio has an undertonal component (e.g. 21/20 = [−2, −1, 1, 1] — has −1 for 5).
            // For undertonal region: any positive non-2 exponent is out of bounds.
            const outsideRegion =
              Array.isArray(candidate.monzo) &&
              (region === "overtonal"
                ? candidate.monzo.slice(1).some((e) => e < 0)
                : region === "undertonal"
                  ? candidate.monzo.slice(1).some((e) => e > 0)
                  : false);
            const isOutOfBounds = outsideTolerance || outsidePrimeLimit || outsideRegion;
            const isCommitted = !!candidate.isCommitted;
            return (
              <button
                key={candidate.ratioText}
                type="button"
                class={[
                  "rationalise-candidate",
                  isCommitted ? "rationalise-candidate--committed" : "",
                  isOutOfBounds ? "rationalise-candidate--out-of-bounds" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => {
                  const parsed = parseExactInterval(candidate.ratioText);
                  onPreviewChangeRef.current?.((prevState) =>
                    setDegreeComparing(
                      setDegreePreview(prevState, degree, candidate.cents),
                      degree,
                      false,
                    ),
                  );
                  setPreviewInterval(parsed);
                  setRationaliseCandidates(null);
                  dragStart.current = null;
                  glideTo(candidate.cents);
                }}
                aria-label={`rational candidate ${candidate.ratioText}`}
              >
                <div class="rationalise-candidate__row1">
                  <span class="rationalise-candidate__ratio">
                    {formatRatioText(candidate.ratioText)}
                  </span>
                  <span class="rationalise-candidate__meta">
                    {candidate.deviation >= 0 ? "+" : ""}
                    {candidate.deviation.toFixed(2)}c
                  </span>
                  {hejiLabel && (
                    <span class="rationalise-candidate__meta rationalise-candidate__heji">
                      {hejiLabel}
                    </span>
                  )}
                </div>
                <div class="rationalise-candidate__row2">
                  <span class="rationalise-candidate__meta">
                    {formatPrimeLimits(candidate.monzo)}
                  </span>
                  <span class="rationalise-candidate__meta">
                    hr {candidate.harmonicRadius.toFixed(2)}
                  </span>
                  <span class="rationalise-candidate__meta">
                    s_ctx {(candidate.contextualConsonance ?? 0).toFixed(2)}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

class MemoTuneCell extends Component {
  shouldComponentUpdate(nextProps) {
    const keys = Object.keys(nextProps);
    return (
      keys.length !== Object.keys(this.props).length ||
      keys.some((key) => !Object.is(nextProps[key], this.props[key]))
    );
  }
  render() {
    return <TuneCell {...this.props} />;
  }
}
const EVENT_PROPS = [
  "onChange",
  "onDegree0Save",
  "onFundamentalChange",
  "onPreviewChange",
  "frequencyAtDegree",
];

// The table also renders live activity and text drafts. Keep those updates out
// of the tuning editors, while every event reads the latest parent callbacks.
export default function StableTuneCell(props) {
  const latest = useRef(props);
  latest.current = props;
  const handlers = useMemo(
    () =>
      Object.fromEntries(
        EVENT_PROPS.map((name) => [name, (...args) => latest.current[name]?.(...args)]),
      ),
    [],
  );
  const { fundamental, heji_anchor_label, heji_anchor_ratio } = props.settings ?? {};
  const settings = useMemo(
    () => ({ fundamental, heji_anchor_label, heji_anchor_ratio }),
    [fundamental, heji_anchor_label, heji_anchor_ratio],
  );
  const degree = props.degree;
  const preview = getDegreePreview(props.previewState, degree);
  const previewState = useMemo(
    () => ({ degreePreviews: { [degree]: preview } }),
    [degree, preview],
  );
  const editorProps = { ...props, settings, previewState };
  // This legacy prop is not consumed by TuneCell.
  delete editorProps.colorSuggestionOptions;
  for (const name of EVENT_PROPS) editorProps[name] = props[name] ? handlers[name] : undefined;
  return <MemoTuneCell {...editorProps} />;
}
